/** Aperçu courriel — logo, pièce, bouton, pied légal. */
import { useState } from "react";
import { companyLetterheadUrl, companyLogoUrl } from "../../company/logoUrl";
import { fillMailText, MAIL_KINDS, type CompanyMail, type MailKind } from "../../email/types";
import { formatSiret } from "../clients/format";
import type { Company } from "../../types";

type EmailPreviewProps = {
  mail: CompanyMail;
  kind: MailKind;
  company: Company;
};

function legalLine(company: Company): string {
  const bits = [
    company.legal_name,
    company.address_line,
    [company.postal_code, company.city].filter(Boolean).join(" "),
    company.siret ? `SIRET ${formatSiret(company.siret)}` : "",
    company.vat_number ? `TVA ${company.vat_number}` : "",
  ].filter(Boolean);
  return bits.join(" · ");
}

export function EmailPreview({ mail, kind, company }: EmailPreviewProps) {
  const [headBroken, setHeadBroken] = useState(false);
  const [markBroken, setMarkBroken] = useState(false);
  const meta = MAIL_KINDS.find((k) => k.id === kind) ?? MAIL_KINDS[0];
  const name = company.legal_name || "Entreprise";
  const vars = {
    client: "Client exemple",
    numero: kind === "quote" ? "D-2026-0001" : kind === "credit_note" ? "A-2026-0001" : kind === "payment" ? "P-2026-0001" : "F-2026-0001",
    montant: "1 200,00 €",
    societe: name,
    date: "16/09/2026",
    echeance: "16/10/2026",
    bouton: "%%BTN%%",
  };
  const subject = fillMailText(mail.templates[kind].subject, { ...vars, bouton: meta.button });
  const body = fillMailText(mail.templates[kind].body || "<p></p>", vars);
  const parts = body.split("%%BTN%%");
  const from = mail.from_name.trim() || name;
  const hasHead = Boolean(company.letterhead_logo_path) && !headBroken;
  const hasMark = Boolean(company.logo_path) && !markBroken;
  const year = new Date().getFullYear();

  return (
    <article className="fx-mail-prev" aria-label="Aperçu du courriel">
      <div className="fx-mail-prev__meta">
        <p>
          <span>De</span>
          <b>{from}</b>
        </p>
        <p>
          <span>À</span>
          <b>contact@client-exemple.fr</b>
        </p>
        {mail.bcc ? (
          <p>
            <span>CCI</span>
            <b>{mail.bcc}</b>
          </p>
        ) : null}
        <p>
          <span>Objet</span>
          <b>{subject}</b>
        </p>
      </div>

      <div className="fx-mail-prev__sheet">
        <div className="fx-mail-prev__brand">
          {hasHead ? (
            <img
              className="fx-mail-prev__logo is-head"
              src={companyLetterheadUrl(company)}
              alt=""
              onError={() => setHeadBroken(true)}
            />
          ) : hasMark ? (
            <img
              className="fx-mail-prev__logo is-mark"
              src={companyLogoUrl(company)}
              alt=""
              onError={() => setMarkBroken(true)}
            />
          ) : (
            <strong className="fx-mail-prev__fallback">{name}</strong>
          )}
        </div>

        <div className="fx-mail-prev__doc">
          <small>{meta.label}</small>
          <b>{vars.numero}</b>
          <strong>{vars.montant}</strong>
        </div>

        <div className="fx-mail-prev__body">
          {parts.map((chunk, i) => (
            <span key={i}>
              <span dangerouslySetInnerHTML={{ __html: chunk }} />
              {i < parts.length - 1 ? <em>{meta.button}</em> : null}
            </span>
          ))}
        </div>

        {mail.attach_pdf ? <p className="fx-mail-prev__att">PDF de la pièce joint</p> : null}

        <footer className="fx-mail-prev__foot">
          <p>{legalLine(company)}</p>
          <p>
            © {year} {name}
          </p>
          <p className="fx-mail-prev__brandmark">
            Édité avec{" "}
            <a href="https://aureliasuite.fr" target="_blank" rel="noopener noreferrer">
              Aurélia Factures
            </a>
          </p>
        </footer>
      </div>
    </article>
  );
}
